import { useCallback } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { useHasPermission } from '../api/session'
import { usePageActions, usePageTitle } from '../AppLayout'
import { useInstrumentAktivitaet } from '../hooks/useInstrumentAktivitaet'
import { useItemQuery } from '../hooks/useItemQuery'
import { useZielwaehrung } from '../hooks/useZielwaehrung'
import { Card } from '../components/card/Card'
import { EmptyState, ErrorBox, Loading } from '../components/common/Feedback'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { InstrumentAnsicht } from '../components/instrument/InstrumentAnsicht'

const PERMISSION = 'instrument:read'

/**
 * Ein Instrument — Stammdaten und sein Umfeld.
 *
 * Die Seite lädt: die Stammdaten mit Kennzahlen und Handelsplätzen, dazu
 * Fälle, News, Einschätzungen, Ideen, Kurse und die Watchlist zum Titel.
 * Wie es aussieht, steht in `InstrumentAnsicht` — dieselbe Komponente
 * rendert die Vorlage im Design-Projekt.
 */
export function InstrumentDetailPage() {
  const allowed = useHasPermission(PERMISSION)
  const { id } = useParams()
  const location = useLocation()

  // Zurück zur Liste MIT ihren Filtern. Der Zeilenklick gibt sie im Zustand
  // mit; ohne ihn — bei einem weitergegebenen Link — bleibt die nackte Liste.
  const back = `/instruments${(location.state as { from?: string } | null)?.from ?? ''}`

  const { waehrung, wahl } = useZielwaehrung(allowed)

  const load = useCallback(
    (signal: AbortSignal) => api.masterDataDetail(
      Number(id), waehrung ? { currency: [waehrung] } : {}, signal),
    [id, waehrung])
  const { item, loading, error, denied, notFound } = useItemQuery(load, allowed)
  const umfeld = useInstrumentAktivitaet(item ?? null)

  // Der Titel kommt aus den Daten und steht in keiner Routentabelle. Solange
  // geladen wird, bleibt es bei "Instrument" — ein leerer Titel wäre ein Loch
  // in der Kopfzeile.
  usePageTitle(item?.name ?? 'Instrument', item?.isin)
  usePageActions(wahl)

  const fallDetail = useCallback(
    (fallId: number, signal: AbortSignal) => api.researchCase(fallId, {}, signal), [])

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION} subject="die Instrumente" />
  }
  if (loading && !item) return <Card span={12}><Loading>lädt Instrument …</Loading></Card>
  if (error) return <ErrorBox>{error}</ErrorBox>
  if (notFound) {
    return (
      <Card span={12}>
        <EmptyState title="Instrument nicht vorhanden">
          Zur Kennung {id} gibt es keinen Datensatz. Vielleicht wurde er
          entfernt, seit der Link entstanden ist.
        </EmptyState>
      </Card>
    )
  }
  if (!item) return null

  return (
    <InstrumentAnsicht instrument={item} umfeld={umfeld.daten} laedt={umfeld.laedt}
                       back={back} fallDetail={fallDetail} />
  )
}
